import { FC } from 'react'
import AppraisalFooter from '../../../../components/custom_components/Appraisal/AppraisalFooter'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import useOneOffPaymentWithObjectives from '../../../../graphql/one-off-payment/hooks/useOneOffPaymentWithObjectives'
import useTargetCorrectionPaymentByMeetingid from '../../../../graphql/target-correction-payment/hooks/useTargetCorrectionPaymentByMeetingid'
import { MeetingStepStatus, MeetingStepViewId, MeetingType, ObjectivePaymentStatus } from '../../../../types'
import { PlannedPaymentContainer } from '../../../objectivePayment/PlannedPaymentContainer'
import { EditableOneOffPayment } from '../../one-off-payment/EditableOneOffPayment'
import { EditableTargetCorrectionPayment } from '../../target-correction-payment/EditableTargetCorrectionPayment'
import { MeetingStepDefaultProps } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { PaymentFooter } from '../shared/PaymentFooter'
import useCountrySettings from '../../../../graphql/countrySettings/hooks/useCountrySettings'
import { existingWrongTCPaymentsForLowEmployee } from '../meeting/util/paymentsUtil'

export const ManagerMeetingStepPayments: FC<MeetingStepDefaultProps> = ({ backBtn, nextBtn, meeting }) => {
  const isAppraisalUpdateMeeting = meeting.type === MeetingType.AppraisalUpdate
  const { loading, error, data } = useOneOffPaymentWithObjectives(meeting.id)

  const { loading_tcp, error_tcp, data_tcp } = useTargetCorrectionPaymentByMeetingid(meeting.id)

  const {
    loading: countrySettingsLoading,
    error: countrySettingsError,
    data: countrySettingsData,
  } = useCountrySettings(meeting.employee.currentContract!.legalEntity.country)

  if (
    loading ||
    error ||
    !data ||
    !meeting.meetingSteps ||
    loading_tcp ||
    error_tcp ||
    !data_tcp ||
    !data_tcp.targetCorrectionPaymentAllowedYears ||
    countrySettingsLoading ||
    countrySettingsError ||
    !countrySettingsData ||
    !countrySettingsData.getCountrySettings
  ) {
    return (
      <LoadingAndErrors
        isLoading={loading || loading_tcp || countrySettingsLoading}
        hasNoData={
          !data ||
          !data_tcp ||
          !data_tcp.targetCorrectionPaymentAllowedYears ||
          !countrySettingsData ||
          !countrySettingsData.getCountrySettings
        }
        error={error || error_tcp || countrySettingsError}
      />
    )
  }

  const targetCorrectionPaymentAllowedYears = data_tcp.targetCorrectionPaymentAllowedYears

  const existingWrongTCPayments = existingWrongTCPaymentsForLowEmployee(
    meeting.appraisalSeason,
    targetCorrectionPaymentAllowedYears,
    data_tcp.targetCorrectionPaymentByMeetingId,
  )

  const showNotOnFirstOfMonthDisclaimer = countrySettingsData.getCountrySettings.paymentsAndChangesDisclaimerVisible

  const objectivePayment = data.objectivePayment
    ? data.objectivePayment
    : {
        id: null,
        meetingId: meeting.id,
        status: ObjectivePaymentStatus.Draft,
        deduction: null,
        paymentDateValid: true,
      }

  const isOneOffPaymentNotAllowed = data.isOneOffPaymentNotAllowed

  return (
    <div data-e2e-manager-meeting-step-payment>
      <MeetingBackground withFooter>
        <PlannedPaymentContainer
          employeeId={meeting.employee.id}
          showNotOnFirstOfMonthDisclaimer={showNotOnFirstOfMonthDisclaimer}
          meeting={meeting}
          earliestPaymentCycle={meeting.nextPaymentCycle}
          objectivePayment={objectivePayment}
          objectives={data.objectivesInMeeting}
          handleLowTag={meeting.requiresSpecialHandlingOfLowTag}
        />
        <EditableOneOffPayment
          meetingId={meeting.id}
          showNotOnFirstOfMonthDisclaimer={showNotOnFirstOfMonthDisclaimer}
          nextPaymentcycle={meeting.nextPaymentCycle}
          oneOffPayment={data.oneOffPaymentByMeetingId ? data.oneOffPaymentByMeetingId : undefined}
          hasDivider={isAppraisalUpdateMeeting}
          isOneOffPaymentNotAllowed={isOneOffPaymentNotAllowed}
        />
        {isAppraisalUpdateMeeting && (
          <EditableTargetCorrectionPayment
            meetingId={meeting.id}
            appraisalSeasonYear={meeting.appraisalSeason.year}
            showNotOnFirstOfMonthDisclaimer={showNotOnFirstOfMonthDisclaimer}
            nextPaymentcycle={meeting.nextPaymentCycle}
            targetCorrectionPayment={data_tcp.targetCorrectionPaymentByMeetingId ? data_tcp.targetCorrectionPaymentByMeetingId : undefined}
            targetCorrectionPaymentAllowedYears={targetCorrectionPaymentAllowedYears}
            existingWrongTCPayments={existingWrongTCPayments}
          />
        )}
      </MeetingBackground>

      <AppraisalFooter>
        <PaymentFooter
          objectives={data.objectivesInMeeting}
          oneOffPayment={
            data.oneOffPaymentByMeetingId && !!data.oneOffPaymentByMeetingId.resultAmount
              ? data.oneOffPaymentByMeetingId.resultAmount
              : undefined
          }
          objectivePayment={data.objectivePayment ? data.objectivePayment : undefined}
          targetCorrectionPayment={
            isAppraisalUpdateMeeting && data_tcp.targetCorrectionPaymentByMeetingId
              ? data_tcp.targetCorrectionPaymentByMeetingId
              : undefined
          }
        />
      </AppraisalFooter>
      <PerFlexRow justify="space-between">
        {backBtn && backBtn()}
        {nextBtn && nextBtn(MeetingStepStatus.Done)}
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.Payments} stepStatus={MeetingStepStatus.Done} />
    </div>
  )
}
